/**
 * Client-side C2PA extraction + validation (privacy-first).
 *
 * Ported from verifieddit-www's c2paClient so the two sites share one trust
 * model. The browser reads and validates the manifest locally with the WASM SDK
 * (@contentauth/c2pa-web); the asset bytes never leave the device.
 *
 * This is what makes multi-gigabyte video workable. `reader.fromBlob` is handed
 * a lazy `File` handle, and the SDK's reader is seek-based — it jumps to the
 * JUMBF/`uuid` box rather than streaming the whole asset, so answering "does
 * this carry provenance, and who signed it?" costs kilobytes regardless of
 * whether the file is 2 MB or 8 GB. Nothing is uploaded to answer that question.
 *
 * Caveat worth keeping in mind: validating the *hard binding* still hashes the
 * byte ranges the data-hash assertion covers, which for video is most of the
 * file. That is a local disk read, not a network transfer — but it is not free,
 * so callers may want to gate it behind an explicit action on very large files.
 */

import { C2PA_WASM_URL, c2paTrustAnchorsUrl } from './c2paConfig'
import {
  calculateTrueValidationStatus,
  isSignerTrusted,
  isHistoricallyAuthentic,
} from './c2paTimestampValidator'
import { detectAiContent, type AiDetectionResult } from './aiContentDetector'

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyManifestStore = any

export interface LocalC2paResult {
  /** True when the asset carries a readable manifest store. */
  hasC2PA: boolean
  /** Raw manifest store — feeds the provenance diagram. */
  manifestStore?: Record<string, unknown>
  /** Signature verified against the embedded chain. */
  isValid: boolean
  /** Signer chains to an anchor in the C2PA trust list. */
  isTrusted: boolean
  /** A verifiable signing timestamp is present. */
  hasTimestamp: boolean
  issuer?: string
  signedAt?: string
  claimGenerator?: string
  /** AI-generation verdict derived from the manifest itself. */
  aiDetection?: AiDetectionResult
  /**
   * Set when the embedded certificate could not be parsed by this WASM build.
   * Manifest structure is still readable; the signature is simply unverified.
   */
  certUnverified?: boolean
}

const UNSIGNED: LocalC2paResult = {
  hasC2PA: false,
  isValid: false,
  isTrusted: false,
  hasTimestamp: false,
}

/** The WASM reports an unsupported embedded cert as a plain Error. */
function isCoseInvalidCertError(e: unknown): boolean {
  return e instanceof Error && e.message.includes('CoseInvalidCert')
}

async function readManifestStore(file: File, withTrust: boolean): Promise<AnyManifestStore> {
  const { createC2pa } = await import('@contentauth/c2pa-web')
  const c2pa = await createC2pa({
    wasmSrc: C2PA_WASM_URL,
    settings: withTrust ? { trust: { trustAnchors: c2paTrustAnchorsUrl() } } : undefined,
  })
  try {
    // `file` is a lazy handle — the SDK seeks within it rather than buffering it.
    const reader = await c2pa.reader.fromBlob(file.type || 'application/octet-stream', file)
    if (!reader) return null
    const ms = await reader.manifestStore()
    await reader.free()
    return ms
  } finally {
    c2pa.dispose()
  }
}

/**
 * Read + validate a file's C2PA manifest entirely in the browser.
 * Returns hasC2PA:false for unsigned media or any read failure (fail-closed).
 */
export async function extractC2paLocally(file: File): Promise<LocalC2paResult> {
  try {
    // Pass 1: full validation against the trust list.
    let ms: AnyManifestStore | 'CERT_INVALID' = await readManifestStore(file, true).catch(
      (e: unknown) => {
        if (isCoseInvalidCertError(e)) return 'CERT_INVALID' as const
        throw e
      },
    )

    let certUnverified = false

    // Pass 2: the asset's own cert is unsupported, not the trust list. Re-read
    // without anchors so the manifest is still legible to the user.
    if (ms === 'CERT_INVALID') {
      certUnverified = true
      ms = await readManifestStore(file, false)
    }

    if (!ms || !ms.manifests || Object.keys(ms.manifests).length === 0) {
      return UNSIGNED
    }

    const active = ms.manifests[ms.active_manifest ?? ''] ?? null
    const validationResults = ms.validation_results

    const isValid = certUnverified
      ? false
      : calculateTrueValidationStatus(validationResults as any, active?.signature_info as any)
    const isTrusted = certUnverified ? false : isSignerTrusted(validationResults as any)
    const hasTimestamp = !!active?.signature_info?.time || isHistoricallyAuthentic(validationResults as any)

    const claimGenerator: string | undefined =
      active?.claim_generator ??
      active?.claim_generator_info?.[0]?.name ??
      undefined

    const assertions: Array<{ label: string; data?: any }> =
      active?.assertions ?? active?.assertion_store ?? []

    return {
      hasC2PA: true,
      manifestStore: ms as Record<string, unknown>,
      isValid,
      isTrusted,
      hasTimestamp,
      issuer: active?.signature_info?.issuer ?? undefined,
      signedAt: active?.signature_info?.time ?? undefined,
      claimGenerator,
      aiDetection: detectAiContent(claimGenerator, assertions),
      certUnverified: certUnverified || undefined,
    }
  } catch (err) {
    // Fail closed: an unreadable manifest is treated as no manifest, never as valid.
    console.warn('[c2pa] local extraction failed:', err)
    return UNSIGNED
  }
}
